import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  List,
  ListItem,
  Button,
  Collapse,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import FolderOpen from '@mui/icons-material/FolderOpen';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import MethodChip from './MethodChip';
import { hexToPastel } from '../../../stylesSheets';

export default function AvailableGroupsDrawer({
  open,
  onClose,
  catalogGroups,
  assignedGroups,
  allClientGroups,
  overrides,
  handleAddGroup,
  handleRemoveGroup,
  handleToggleOverride,
  isLoadingAllGroups,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const getPastelColor = (colorHex) => {
    try {
      return hexToPastel(colorHex);
    } catch (e) {
      return colorHex;
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const getOverride = (endpoint) =>
    overrides.find(
      (o) => o.path === endpoint.path && o.method === endpoint.method
    );

  // Filter groups according to search term (by group name or endpoint path)
  const filteredCatalogGroups = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return catalogGroups.filter(
      (group) =>
        group.group_name.toLowerCase().includes(term) ||
        group.endpoints.some((ep) => ep.path.toLowerCase().includes(term))
    );
  }, [catalogGroups, searchTerm]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Box
        sx={{
          width: { xs: 320, sm: 420 },
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('cws_app.management.available_groups', 'Available Groups')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t('cws_app.management.search_groups_placeholder', 'Search groups or endpoints')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Scrollable List Container */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          {isLoadingAllGroups ? (
            <LinearProgress color="secondary" sx={{ mb: 2, borderRadius: '10rem' }} />
          ) : (
            <List disablePadding>
              {filteredCatalogGroups.map((group) => {
                const isAssigned = assignedGroups.includes(group.group_name);
                const isExpanded = !!expandedGroups[group.group_name];

                return (
                  <PaperCard key={group.group_name}>
                    {/* Card Header Row */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      sx={{ py: 1, px: 2 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <FolderOpen fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography fontWeight="bold" variant="body1">
                          {group.group_name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <GroupAddRemoveButton
                          groupName={group.group_name}
                          isAssigned={isAssigned}
                          onAdd={handleAddGroup}
                          onRemove={handleRemoveGroup}
                        />
                        <IconButton size="small" onClick={() => handleToggleGroup(group.group_name)}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Collapsible Endpoints inside Card */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                      <Divider />
                      <List disablePadding sx={{ backgroundColor: theme.palette.action.hover }}>
                        {group.endpoints.map((ep) => {
                          const override = getOverride(ep);
                          const isGranted = override?.type === 'grant';
                          const isRevoked = override?.type === 'revoke';

                          return (
                            <ListItem
                              key={`${ep.method}-${ep.path}`}
                              dense
                              sx={{
                                pl: 3,
                                pr: 2,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'action.selected',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                '&:last-child': {
                                  borderBottom: 'none',
                                },
                              }}
                            >
                              {/* Endpoint Method + Path */}
                              <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
                                <MethodChip method={ep.method} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {ep.path}
                                </Typography>
                              </Box>

                              {/* Actions / Status matching the image design */}
                              <Box display="flex" alignItems="center" gap={1}>
                                {isAssigned ? (
                                  <>
                                    {isRevoked ? (
                                      <>
                                        <Chip
                                          label={t('cws_app.management.revoked', 'REVOKED')}
                                          size="small"
                                          sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            height: 20,
                                            backgroundColor: getPastelColor(theme.palette.error.main),
                                            color: theme.palette.error.dark,
                                            border: '1px solid',
                                            borderColor: theme.palette.error.main,
                                          }}
                                        />
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => handleToggleOverride(ep, 'revoke')}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            borderRadius: '4px',
                                            py: 0.1,
                                            px: 1,
                                            height: 20,
                                            backgroundColor: theme.palette.success.main,
                                            color: theme.palette.success.contrastText,
                                            '&:hover': {
                                              backgroundColor: theme.palette.success.dark,
                                            },
                                          }}
                                        >
                                          {t('cws_app.management.restore', 'RESTORE')}
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Chip
                                          label={t('cws_app.management.by_group', 'BY GROUP')}
                                          size="small"
                                          sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            height: 20,
                                            backgroundColor: theme.palette.action.selected,
                                            color: theme.palette.text.secondary,
                                          }}
                                        />
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            borderRadius: '4px',
                                            py: 0.1,
                                            px: 1.2,
                                            height: 20,
                                            backgroundColor: theme.palette.action.disabledBackground,
                                            color: theme.palette.action.disabled,
                                          }}
                                        >
                                          {t('cws_app.management.grant', 'GRANT')}
                                        </Button>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {isGranted ? (
                                      <>
                                        <Chip
                                          label={t('cws_app.management.granted_status', 'GRANTED')}
                                          size="small"
                                          sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            height: 20,
                                            backgroundColor: getPastelColor(theme.palette.success.main),
                                            color: theme.palette.success.dark,
                                            border: '1px solid',
                                            borderColor: theme.palette.success.main,
                                          }}
                                        />
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => handleToggleOverride(ep, 'grant')}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            color: theme.palette.secondary.main,
                                            minWidth: 'auto',
                                            px: 0.5,
                                            height: 20,
                                            '&:hover': {
                                              backgroundColor: 'rgba(255, 2, 102, 0.04)',
                                            },
                                          }}
                                        >
                                          ✕ {t('General.remove', 'REMOVE')}
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleToggleOverride(ep, 'grant')}
                                        sx={{
                                          textTransform: 'none',
                                          fontWeight: 'bold',
                                          fontSize: '0.7rem',
                                          borderRadius: '4px',
                                          py: 0.1,
                                          px: 1.2,
                                          height: 20,
                                          backgroundColor: theme.palette.success.main,
                                          color: theme.palette.success.contrastText,
                                          '&:hover': {
                                            backgroundColor: theme.palette.success.dark,
                                          },
                                        }}
                                      >
                                        + {t('cws_app.management.grant', 'GRANT')}
                                      </Button>
                                    )}
                                  </>
                                )}
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </PaperCard>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer Area with Done Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.background.paper,
              fontWeight: 'bold',
              textTransform: 'none',
              px: 4,
              py: 0.75,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: theme.palette.text.secondary,
              },
            }}
          >
            {t('General.done', 'DONE')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// Sub-styled component to look exactly like the cards in the design images
function PaperCard({ children }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: '8px',
        mb: 1.5,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
      }}
    >
      {children}
    </Box>
  );
}

// Interactive Button to Add or Remove whole groups directly in-place inside the Drawer
function GroupAddRemoveButton({ groupName, isAssigned, onAdd, onRemove }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const getPastelColor = (colorHex) => {
    try {
      return hexToPastel(colorHex);
    } catch (e) {
      return colorHex;
    }
  };

  if (isAssigned) {
    const baseColor = hovered ? theme.palette.error.main : theme.palette.success.main;
    const pastelBg = getPastelColor(baseColor);
    const textColor = hovered ? theme.palette.error.dark : theme.palette.success.dark;

    return (
      <Button
        size="small"
        variant="contained"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onRemove(groupName)}
        sx={{
          fontSize: '0.65rem',
          fontWeight: 'bold',
          height: 22,
          textTransform: 'none',
          borderRadius: '4px',
          px: 1.5,
          backgroundColor: pastelBg,
          color: textColor,
          border: '1px solid',
          borderColor: baseColor,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: pastelBg,
            boxShadow: 'none',
          }
        }}
      >
        {hovered ? `✕ ${t('General.remove', 'REMOVE')}` : `✓ ${t('General.added', 'ADDED')}`}
      </Button>
    );
  }

  return (
    <Button
      size="small"
      variant="contained"
      onClick={() => onAdd(groupName)}
      sx={{
        backgroundColor: theme.palette.text.primary,
        color: theme.palette.background.paper,
        fontWeight: 'bold',
        textTransform: 'none',
        fontSize: '0.7rem',
        py: 0.2,
        px: 1.5,
        borderRadius: '4px',
        height: 22,
        '&:hover': {
          backgroundColor: theme.palette.text.secondary,
        },
      }}
    >
      + {t('General.add', 'ADD')}
    </Button>
  );
}
